import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronLeft } from 'lucide-react';
import { SEO } from '@/components/SEO';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <SEO title="Privacy Policy — Plan My Garden" description="How Plan My Garden collects, uses, and safeguards your personal information." path="/privacy" />

      <div className="p-4 md:p-8">
        <div className="mx-auto max-w-2xl">
          <div className="mb-8 flex items-center gap-3">
            <Button variant="ghost" size="icon" aria-label="Go back" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
              Privacy Policy
            </h1>
          </div>

          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4">
            <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

            <h2 className="text-lg font-semibold text-foreground">1. Introduction</h2>
            <p>Plan My Garden ("we", "our", "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our application.</p>

            <h2 className="text-lg font-semibold text-foreground">2. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-foreground">Account information:</strong> Email address and display name when you create an account.</li>
              <li><strong className="text-foreground">Garden data:</strong> Information about your plants, garden setup, growing zone, and gardening preferences.</li>
              <li><strong className="text-foreground">Payment information:</strong> Billing details processed securely through our payment provider, Paddle. We do not store your payment card details.</li>
              <li><strong className="text-foreground">Usage data:</strong> How you interact with the application to improve our service.</li>
            </ul>

            <h2 className="text-lg font-semibold text-foreground">3. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide and maintain the application</li>
              <li>Generate personalised gardening schedules and recommendations</li>
              <li>Process payments and manage subscriptions</li>
              <li>Send important service updates</li>
              <li>Improve our application and user experience</li>
            </ul>

            <h2 className="text-lg font-semibold text-foreground">4. Data Sharing</h2>
            <p>We do not sell your personal data. We share data only with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-foreground">Paddle:</strong> Our payment processor, for billing and subscription management.</li>
              <li><strong className="text-foreground">Service providers:</strong> Who help us operate the application (hosting, database).</li>
            </ul>

            <h2 className="text-lg font-semibold text-foreground">5. Data Security</h2>
            <p>We implement appropriate technical and organisational measures to protect your data, including encryption in transit and at rest, and secure authentication.</p>

            <h2 className="text-lg font-semibold text-foreground">6. Your Rights</h2>
            <p>You have the right to access, correct, or delete your personal data. You can delete your account at any time through the application settings. To exercise your rights, contact us at the email below.</p>

            <h2 className="text-lg font-semibold text-foreground">7. Cookies</h2>
            <p>We use essential cookies for authentication and session management. We do not use tracking or advertising cookies.</p>

            <h2 className="text-lg font-semibold text-foreground">8. Changes to This Policy</h2>
            <p>We may update this policy from time to time. We will notify you of significant changes through the application.</p>

            <h2 className="text-lg font-semibold text-foreground">9. Contact</h2>
            <p>If you have questions about this Privacy Policy, please contact us through the application.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
